import React, { useState, useEffect } from "react";
import { useTheme } from "@/contexts/ThemeContext";
import { useFirmDataEnabled } from "@/hooks/useFirmDataEnabled";
import { useAuth } from "@/contexts/AuthContext";
import "./index.scss";
import { getItemWithExpiry } from "@/util/util";
import {
  message,
  Upload,
  Alert,
  Table,
  Pagination,
  Button,
  Progress,
  Input,
  Tabs,
  Modal,
  Space,
  Dropdown,
} from "antd";
import type { TableProps, PaginationProps, TabsProps } from "antd";
import {
  DeleteOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  FileImageOutlined,
  FileZipOutlined,
  FileTextOutlined,
  SearchOutlined,
  RightOutlined,
  CloudDownloadOutlined,
  CloudUploadOutlined,
  FilterOutlined,
  DownOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import type {
  UploadFile,
  RcFile,
  UploadChangeParam,
} from "antd/es/upload/interface";
import {
  getFilehubPresignedURL,
  confirmUploadFile,
  // getAdvisorFiles,
  // getReceivedAdvisorFiles,
  deleteEnduserFile,
  getSharedFileDownloadLink,
  getFilehubPresignedURLResponse,
  FilehubFilesData,
  getAdvisorFilesWithShares,
  getReceivedAdvisorFilesWithShares,
  getApexDocuments,
  ApexDocument,
} from "@/api/custom";
import { checkUserSignedUp, getTaxReturnFiles } from "@/services/tax-service";
import File from "@/assets/cloud_upload_pink.svg";
import DownloadIcon from "@/assets/save_alt.svg";
import UploadIcon from "@/assets/upload.svg";

import dayjs from "dayjs";
import { formatFileSize } from "@wavvest/shared-utils";

interface EnhancedFilehubFilesData extends FilehubFilesData {
  source: "sent" | "received";
}

// A tax return that exists for a given year (federal or a state return).
// The actual download link is fetched fresh on click (April links expire ~5 min).
interface TaxReturnDoc {
  key: string;
  year: number;
  name: string;
  type: "federal" | "state";
}

/** Build display name: "Document Type, Month Year, document name" from APEX doc payload. */
/** Prefer investor_document (document_type + process_date), then account_document (document_type + uploaded_date), then id_document (document_type + uploaded_date). */
function getApexDocumentDisplayName(doc: ApexDocument): string {
  let documentType: string | undefined;
  let datePayload: { year: number; month: number; day: number } | undefined;

  if (doc.investor_document) {
    documentType = doc.investor_document.document_type;
    datePayload = doc.investor_document.process_date;
  } else if (doc.account_document) {
    documentType = doc.account_document.document_type;
    datePayload = doc.account_document.uploaded_date;
  } else if (doc.id_document) {
    documentType = doc.id_document.document_type;
    datePayload = doc.id_document.uploaded_date;
  }

  const typeLabel = documentType
    ? documentType
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : "Document";

  const monthYear = datePayload
    ? dayjs()
        .year(datePayload.year)
        .month(datePayload.month - 1)
        .format("MMMM YYYY")
    : "";

  return [typeLabel, monthYear].filter(Boolean).join(", ");
}

/** Same priority as display name: investor process_date, account uploaded_date, id uploaded_date. Returns YYYY-MM-DD or "". */
function getApexDocumentDate(doc: ApexDocument): string {
  const datePayload =
    doc.investor_document?.process_date ??
    doc.account_document?.uploaded_date ??
    doc.id_document?.uploaded_date;
  return datePayload
    ? dayjs()
        .year(datePayload.year)
        .month(datePayload.month - 1)
        .date(datePayload.day)
        .format("MMMM DD, YYYY")
    : "";
}

const FileHub: React.FC = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [firmId, setFirmId] = useState<string>("");
  const { enabled: firmDataEnabled, loading: firmDataLoading } =
    useFirmDataEnabled(firmId || undefined);
  // basic state
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [activeTab, setActiveTab] = useState("all");
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [advisorId, setAdvisorId] = useState<string>("");
  const [pendingFile, setPendingFile] = useState<RcFile | null>(null);

  // file data state
  const [sentFiles, setSentFiles] = useState<EnhancedFilehubFilesData[]>([]);
  const [receivedFiles, setReceivedFiles] = useState<
    EnhancedFilehubFilesData[]
  >([]);
  const [filteredSentFiles, setFilteredSentFiles] = useState<
    EnhancedFilehubFilesData[]
  >([]);
  const [filteredReceivedFiles, setFilteredReceivedFiles] = useState<
    EnhancedFilehubFilesData[]
  >([]);
  const [combinedData, setCombinedData] = useState<EnhancedFilehubFilesData[]>(
    [],
  );
  const [filteredData, setFilteredData] = useState<EnhancedFilehubFilesData[]>(
    [],
  );

  // APEX documents state
  const [apexDocuments, setApexDocuments] = useState<ApexDocument[]>([]);

  // Tax return documents (April) state
  const [taxDocuments, setTaxDocuments] = useState<TaxReturnDoc[]>([]);

  // pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sentPage, setSentPage] = useState(1);
  const [sentPageSize, setSentPageSize] = useState(10);
  const [sentTotalPage, setSentTotalPage] = useState(0);
  const [sentTotalCount, setSentTotalCount] = useState(0);
  const [receivedPage, setReceivedPage] = useState(1);
  const [receivedPageSize, setReceivedPageSize] = useState(10);
  const [receivedTotalPage, setReceivedTotalPage] = useState(0);
  const [receivedTotalCount, setReceivedTotalCount] = useState(0);

  // search and sort state
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  // const [sortBy, setSortBy] = useState<'upload_at' | 'filename' | 'share_count'>('upload_at');
  const sortOrder: "ascend" | "descend" | null = "descend";
  // const [sentTablePaginationKey, setSentTablePaginationKey] = useState<string>();
  // const [receivedTablePaginationKey, setReceivedTablePaginationKey] = useState<string>();

  const [isMobile, setIsMobile] = useState(false);
  const [swipedCardKey, setSwipedCardKey] = useState<string | null>(null);
  const touchStartXRef = React.useRef<number>(0);
  const touchCurrentXRef = React.useRef<number>(0);
  const swipeJustHappenedRef = React.useRef<boolean>(false);
  const SWIPE_THRESHOLD = 60;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(max-width: 768px)");
    setIsMobile(mql.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  const searchTimeoutRef = React.useRef<NodeJS.Timeout>();

  const fetchAllSentFiles = async (): Promise<EnhancedFilehubFilesData[]> => {
    try {
      let allFiles: EnhancedFilehubFilesData[] = [];
      let hasMore = true;
      let paginationKey: string = "";

      setLoading(true);

      while (hasMore) {
        const response = await getAdvisorFilesWithShares({
          limit: sentPageSize.toString(),
          pagination_key: paginationKey,
          // sort_by: sortBy,
          // search_keyword: searchKeyword,
          sort_order: sortOrder === "ascend" ? "asc" : "desc",
          include_share_details: "true",
        });

        const filesWithSource = response.files.map((file) => ({
          ...file,
          source: "sent" as const,
        }));

        allFiles = [...allFiles, ...filesWithSource];

        paginationKey = response.pagination_key;
        hasMore = response.has_more;
      }

      return allFiles;
    } catch (error) {
      console.error("Error fetching all sent files:", error);
      setError("Failed to load all sent files");
      return [];
    }
  };

  const fetchAllReceivedFiles = async (): Promise<
    EnhancedFilehubFilesData[]
  > => {
    try {
      let allFiles: EnhancedFilehubFilesData[] = [];
      let hasMore = true;
      let paginationKey: string = "";

      setLoading(true);

      while (hasMore) {
        const response = await getReceivedAdvisorFilesWithShares({
          limit: receivedPageSize.toString(),
          pagination_key: paginationKey,
          // sort_by: sortBy,
          sort_order: sortOrder === "ascend" ? "asc" : "desc",
          // search_keyword: searchKeyword,
          include_share_details: "true",
        });

        const filesWithSource = response.files.map((file) => ({
          ...file,
          source: "received" as const,
        }));

        allFiles = [...allFiles, ...filesWithSource];

        paginationKey = response.pagination_key;
        hasMore = response.has_more;
      }

      return allFiles;
    } catch (error) {
      console.error("Error fetching all received files:", error);
      setError("Failed to load all received files");
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Load prior-year April tax return files (federal/state) grouped by year.
  // Self-gates on April signup so non-tax users fetch nothing.
  const fetchTaxDocuments = async (email: string): Promise<TaxReturnDoc[]> => {
    try {
      const isSignedUp = await checkUserSignedUp(email);
      if (!isSignedUp) return [];

      const currentYear = new Date().getFullYear();
      // Most recent filed year back to 2024 (matches the mobile vault floor).
      const years = Array.from(
        { length: Math.max(0, currentYear - 2024) },
        (_, i) => currentYear - 1 - i,
      );

      const perYear = await Promise.all(
        years.map(async (year) => {
          try {
            const res = await getTaxReturnFiles(
              email,
              "FILING",
              year.toString(),
            );
            return { year, files: res.files };
          } catch {
            // No return for this year (or not available yet) — skip it.
            return null;
          }
        }),
      );

      const docs: TaxReturnDoc[] = [];
      perYear.forEach((entry) => {
        if (!entry?.files) return;
        const { year, files } = entry;
        if (files.federalFileLink) {
          docs.push({
            key: `${year}-federal`,
            year,
            name: `${year} Federal Tax Return`,
            type: "federal",
          });
        }
        if (files.isStateFileNeeded && files.stateFileLink) {
          docs.push({
            key: `${year}-state`,
            year,
            name: `${year} State Tax Return`,
            type: "state",
          });
        }
      });
      return docs;
    } catch (error) {
      console.error("Error fetching tax documents:", error);
      return [];
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      const [allSentFiles, allReceivedFiles, apexDocs, taxDocs] =
        await Promise.all([
          // fetchAllSentFiles(searchKeyword),
          // fetchAllReceivedFiles(searchKeyword)
          fetchAllSentFiles(),
          fetchAllReceivedFiles(),
          firmDataEnabled
            ? Promise.resolve({ document_records: [] })
            : getApexDocuments().catch(() => ({ document_records: [] })),
          user?.email ? fetchTaxDocuments(user.email) : Promise.resolve([]),
        ]);

      setSentFiles(allSentFiles);
      setReceivedFiles(allReceivedFiles);
      setApexDocuments(
        apexDocs.document_records
          .filter((doc) => {
            if (!doc.id_document) {
              return true;
            }
            return doc.id_document.document_type !== "THIRD_PARTY_CIP_RESULTS";
          })
          .map((doc) => ({
            ...doc,
            document_id: doc.document_id ?? doc.name ?? "",
            document_name: getApexDocumentDisplayName(doc),
            document_date: getApexDocumentDate(doc),
            download_uri: doc.download_uri ?? doc.download_link,
          })),
      );

      setTaxDocuments(taxDocs);

      setFilteredSentFiles(allSentFiles);
      setFilteredReceivedFiles(allReceivedFiles);

      setSentTotalCount(allSentFiles.length);
      setReceivedTotalCount(allReceivedFiles.length);

      setSentTotalPage(Math.ceil(allSentFiles.length / sentPageSize));
      setReceivedTotalPage(
        Math.ceil(allReceivedFiles.length / receivedPageSize),
      );

      const combined = [...allSentFiles, ...allReceivedFiles];
      setCombinedData(combined);
      setFilteredData(combined);
    } catch (error) {
      console.error("Error fetching files:", error);
      setError("Failed to load files data");
      setSentFiles([]);
      setReceivedFiles([]);
      setApexDocuments([]);
      setTaxDocuments([]);
      setFilteredSentFiles([]);
      setFilteredReceivedFiles([]);
    } finally {
      setLoading(false);
    }
  };

  // fetch data when component is loaded
  useEffect(() => {
    const userStr = getItemWithExpiry("user");
    const user = userStr ? JSON.parse(userStr) : null;

    setAdvisorId(user?.client_id || "");
    setFirmId(user?.client_id || "");
  }, []);

  useEffect(() => {
    if (firmDataEnabled && activeTab === "apex-statements") {
      setActiveTab("all");
    }
  }, [activeTab, firmDataEnabled]);

  useEffect(() => {
    if (firmId && !firmDataLoading) {
      fetchData();
    }
    // user?.email is included so tax documents load once the auth session
    // hydrates (AuthContext resolves user asynchronously, possibly after firmId).
  }, [firmDataEnabled, firmDataLoading, firmId, user?.email]);

  // handle search function
  const handleSearch = (value: string) => {
    setSearchKeyword(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      if (value) {
        const keyword = value.toLowerCase();

        // web search
        const newFilteredSentFiles = sentFiles.filter((file) =>
          file.filename.toLowerCase().includes(keyword),
        );

        const newFilteredReceivedFiles = receivedFiles.filter((file) =>
          file.filename.toLowerCase().includes(keyword),
        );

        setFilteredSentFiles(newFilteredSentFiles);
        setFilteredReceivedFiles(newFilteredReceivedFiles);

        const combined = [...newFilteredSentFiles, ...newFilteredReceivedFiles];
        setFilteredData(combined);

        // reset pagination
        setCurrentPage(1);
        setSentPage(1);
        setReceivedPage(1);

        // update count
        setSentTotalCount(newFilteredSentFiles.length);
        setReceivedTotalCount(newFilteredReceivedFiles.length);
        setSentTotalPage(Math.ceil(newFilteredSentFiles.length / sentPageSize));
        setReceivedTotalPage(
          Math.ceil(newFilteredReceivedFiles.length / receivedPageSize),
        );
      } else {
        // reset when no search keyword
        setFilteredSentFiles(sentFiles);
        setFilteredReceivedFiles(receivedFiles);

        const combined = [...sentFiles, ...receivedFiles];
        setFilteredData(combined);

        // reset pagination
        setCurrentPage(1);
        setSentPage(1);
        setReceivedPage(1);

        // update count
        setSentTotalCount(sentFiles.length);
        setReceivedTotalCount(receivedFiles.length);
        setSentTotalPage(Math.ceil(sentFiles.length / sentPageSize));
        setReceivedTotalPage(
          Math.ceil(receivedFiles.length / receivedPageSize),
        );
      }
    }, 300);
  };

  // clear search timeout
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // file sort function
  // const sortFiles = (files: EnhancedFilehubFilesData[], sortField: string, order: 'ascend' | 'descend' | null) => {
  //   if (!order) return files;

  //   return [...files].sort((a, b) => {
  //     if (sortField === 'filename') {
  //       return order === 'ascend'
  //         ? a.filename.localeCompare(b.filename)
  //         : b.filename.localeCompare(a.filename);
  //     } else if (sortField === 'upload_at') {
  //       return order === 'ascend'
  //         ? new Date(a.upload_at).getTime() - new Date(b.upload_at).getTime()
  //         : new Date(b.upload_at).getTime() - new Date(a.upload_at).getTime();
  //     }
  //     return 0;
  //   });
  // };

  // handle table sort
  // const handleSort = (sorter: any) => {
  //   if (sorter.field) {
  //     const newSortBy = sorter.field as 'upload_at' | 'filename' | 'share_count';
  //     const newSortOrder = sorter.order as 'ascend' | 'descend' | null;

  //     setSortBy(newSortBy);
  //     setSortOrder(newSortOrder);

  //     // client sort
  //     const sortedSentFiles = sortFiles(filteredSentFiles, newSortBy, newSortOrder);
  //     const sortedReceivedFiles = sortFiles(filteredReceivedFiles, newSortBy, newSortOrder);

  //     setFilteredSentFiles(sortedSentFiles);
  //     setFilteredReceivedFiles(sortedReceivedFiles);

  //     // merge sorted data
  //     const sortedCombined = [...sortedSentFiles, ...sortedReceivedFiles];
  //     setFilteredData(sortedCombined);
  //   }
  // };

  // handle file upload request
  // const customRequest = async ({ file, onSuccess, onError }: any) => {
  //   handleUploadFile(file as RcFile, onSuccess, onError);
  // };

  // handle file status change
  const handleChange = (info: UploadChangeParam<UploadFile>) => {
    let updatedFileList = [...info.fileList];
    updatedFileList = updatedFileList.map((file) => {
      if (file.response) {
        file.status = "done";
      }
      return file;
    });

    setFileList(updatedFileList);
  };

  // preview file
  // const handlePreview = async (record: FilehubFilesData) => {
  //   try {
  //     const downloadLinkResponse = await getSharedFileDownloadLink(record.key);
  //     window.open(downloadLinkResponse.preview_url, '_blank');
  //   } catch (error) {
  //     console.error("Failed to preview shared file:", error);
  //     message.error("Failed to preview file");
  //   }
  // };

  // download file
  const handleDownload = async (record: FilehubFilesData) => {
    try {
      const downloadLinkResponse = await getSharedFileDownloadLink(record.key);
      // The backend's download_url embeds the raw filename in the S3
      // response-content-disposition header, which S3 rejects with
      // InvalidArgument when the name contains non ISO-8859-1 characters
      // (e.g. Unicode spaces in macOS screenshots). That made every
      // non-plain-ASCII-named file fail to download. Fetch the object via the
      // preview_url (no content-disposition) and save it client-side so the
      // filename is applied locally and every file type downloads reliably.
      const response = await fetch(downloadLinkResponse.preview_url);
      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = record.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Failed to download shared file:", error);
      message.error("Failed to download file");
    }
  };

  // delete file
  const handleDelete = async (record: FilehubFilesData) => {
    Modal.confirm({
      title: "Delete Confirmation",
      content: `Are you sure you want to delete ${record.filename}?`,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteEnduserFile(record.key);

          setSentFiles((prevFiles) => {
            const newSentFiles = prevFiles.filter(
              (file) => file.key !== record.key,
            );

            setFilteredSentFiles((prev) =>
              prev.filter((file) => file.key !== record.key),
            );

            setCombinedData((prev) =>
              prev.filter((file) => file.key !== record.key),
            );

            setFilteredData((prev) =>
              prev.filter((file) => file.key !== record.key),
            );

            setSentTotalCount(newSentFiles.length);
            setSentTotalPage(Math.ceil(newSentFiles.length / sentPageSize));

            return newSentFiles;
          });

          message.success(`${record.filename} deleted successfully`);
        } catch (error) {
          console.error("Delete failed:", error);
          message.error(`Failed to delete ${record.filename}`);
        }
      },
    });
  };

  // download APEX document
  const handleApexDownload = async (record: ApexDocument) => {
    try {
      if (!record.download_uri) {
        message.error("Download URL not available for this document");
        return;
      }

      window.open(record.download_uri, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Failed to download APEX document:", error);
      message.error("Failed to download APEX document");
    }
  };

  // download tax return document. April download links expire after ~5 minutes,
  // so fetch a fresh link on click rather than reusing the one loaded with the page.
  const handleTaxDownload = async (record: TaxReturnDoc) => {
    if (!user?.email) {
      message.error("Unable to get user information");
      return;
    }
    // Open the tab synchronously (within the click gesture) so it isn't
    // popup-blocked, then point it at the freshly generated link.
    const downloadWindow = window.open("about:blank", "_blank");
    try {
      const res = await getTaxReturnFiles(
        user.email,
        "FILING",
        record.year.toString(),
      );
      const link =
        record.type === "federal"
          ? res.files.federalFileLink
          : res.files.stateFileLink;
      if (!link) {
        downloadWindow?.close();
        message.error("Download link is no longer available.");
        return;
      }
      if (downloadWindow) {
        downloadWindow.opener = null;
        downloadWindow.location.href = link;
      } else {
        window.open(link, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      console.error("Failed to download tax document:", error);
      downloadWindow?.close();
      message.error("Failed to download tax document. Please try again.");
    }
  };

  // handle pagination
  const handleSentPaginationChange: PaginationProps["onChange"] = (
    page,
    pageSize,
  ) => {
    setSentPage(page);
    setSentPageSize(pageSize);
    setSentTotalPage(Math.ceil(filteredSentFiles.length / pageSize));
  };

  const handleReceivedPaginationChange: PaginationProps["onChange"] = (
    page,
    pageSize,
  ) => {
    setReceivedPage(page);
    setReceivedPageSize(pageSize);
    setReceivedTotalPage(Math.ceil(filteredReceivedFiles.length / pageSize));
  };

  const handlePaginationChange: PaginationProps["onChange"] = (
    page,
    pageSize,
  ) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  // get paginated data
  const getPaginatedData = (
    files: EnhancedFilehubFilesData[],
    page: number,
    size: number,
  ) => {
    return files.slice((page - 1) * size, page * size);
  };

  // handle tab change
  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  // get file icon
  const getFileIcon = (filename: string) => {
    const extension = filename.split(".").pop()?.toLowerCase() || "";

    switch (extension) {
      case "pdf":
        return <FilePdfOutlined style={{ fontSize: 20, color: "#ff4d4f" }} />;
      case "doc":
      case "docx":
        return <FileWordOutlined style={{ fontSize: 20, color: "#1890ff" }} />;
      case "xls":
      case "xlsx":
        return <FileExcelOutlined style={{ fontSize: 20, color: "#52c41a" }} />;
      case "ppt":
      case "pptx":
        return <FilePptOutlined style={{ fontSize: 20, color: "#fa8c16" }} />;
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return <FileImageOutlined style={{ fontSize: 20, color: "#722e9a" }} />;
      case "zip":
      case "rar":
        return <FileZipOutlined style={{ fontSize: 20, color: "#faad14" }} />;
      case "txt":
        return <FileTextOutlined style={{ fontSize: 20, color: "#bfbfbf" }} />;
      default:
        return <FileOutlined style={{ fontSize: 20, color: "#8c8c8c" }} />;
    }
  };

  // table column definition
  const documentColumns: TableProps<FilehubFilesData>["columns"] = [
    {
      title: "Name",
      dataIndex: "filename",
      key: "filename",
      // sorter: true,
      // sortOrder: sortBy === 'filename' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginRight: 8 }}>{getFileIcon(record.filename)}</span>
          <span style={{ fontWeight: 500 }}>{text}</span>
        </div>
      ),
    },
    // {
    //   title: 'Type',
    //   dataIndex: 'type',
    //   key: 'type',
    //   render: (_, record) => {
    //     const fileExtension = record.filename.split('.').pop()?.toUpperCase() || 'Unknown';
    //     return fileExtension;
    //   }
    // },
    {
      title: "Date",
      dataIndex: "upload_at",
      key: "upload_at",
      render: (value) => {
        if (value) {
          return dayjs(value).format("YYYY-MM-DD");
        }
        return "N/A";
      },
    },
    {
      title: "Size",
      dataIndex: "size_byte",
      key: "size_byte",
      render: (value) => formatFileSize(value),
    },
    {
      title: "",
      key: "actions",
      render: (_, record) => (
        <Space>
          {/* <Button 
            type="link" 
            onClick={() => handlePreview(record)}
          >
            Preview
          </Button> */}
          <Button
            type="link"
            // icon={<DownloadOutlined />}
            icon={
              <img
                src={DownloadIcon}
                alt="Download"
                style={{ width: 24, height: 24 }}
              />
            }
            onClick={() => handleDownload(record)}
          />
          {record.owner_type !== "advisor" && (
            <Button
              type="link"
              icon={<DeleteOutlined />}
              danger
              onClick={() => handleDelete(record)}
            />
          )}
        </Space>
      ),
    },
  ];

  // APEX document columns
  const apexDocumentColumns: TableProps<ApexDocument>["columns"] = [
    {
      title: "Name",
      dataIndex: "document_name",
      key: "document_name",
    },
    {
      title: "Date",
      dataIndex: "document_date",
      key: "document_date",
      render: (value: string) => value ?? "",
    },
    {
      title: "Download",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={
              <img
                src={DownloadIcon}
                alt="Download"
                style={{ width: 24, height: 24 }}
              />
            }
            onClick={() => handleApexDownload(record)}
          />
        </Space>
      ),
    },
  ];

  // tax return document columns
  const taxDocumentColumns: TableProps<TaxReturnDoc>["columns"] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Tax Year",
      dataIndex: "year",
      key: "year",
    },
    {
      title: "Download",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={
              <img
                src={DownloadIcon}
                alt="Download"
                style={{ width: 24, height: 24 }}
              />
            }
            onClick={() => handleTaxDownload(record)}
          />
        </Space>
      ),
    },
  ];

  // all files table column definition
  const allColumns: TableProps<EnhancedFilehubFilesData>["columns"] = [
    {
      title: "Name",
      dataIndex: "filename",
      key: "filename",
      // sorter: true,
      // sortOrder: sortBy === 'filename' ? sortOrder : null,
      render: (text: string, record: FilehubFilesData) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginRight: 8 }}>{getFileIcon(record.filename)}</span>
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: "Date",
      dataIndex: "upload_at",
      key: "upload_at",
      // sorter: true,
      // sortOrder: sortBy === 'upload_at' ? sortOrder : null,
      render: (date: string) => dayjs(date).format("MMM D, YYYY"),
    },
    {
      title: "Type",
      dataIndex: "source",
      key: "source",
      render: (_: unknown, record: EnhancedFilehubFilesData) => (
        // <Tag color={record.source === 'sent' ? 'blue' : 'green'}>
        <div className="text-center">
          {record.source === "sent" ? "Sent" : "Received"}
        </div>
        // </Tag>
      ),
    },
    {
      title: "",
      key: "action",
      render: (_: unknown, record: EnhancedFilehubFilesData) => (
        <Space>
          {/* <Button type="link" onClick={() => handlePreview(record)}>Preview</Button> */}
          <Button
            type="link"
            // icon={<DownloadOutlined />}
            icon={
              <img
                src={DownloadIcon}
                alt="Download"
                style={{ width: 24, height: 24 }}
              />
            }
            onClick={() => handleDownload(record)}
          />
          {record.source === "sent" && (
            <Button
              type="link"
              icon={<DeleteOutlined />}
              danger
              onClick={() => handleDelete(record)}
            />
          )}
        </Space>
      ),
    },
  ];

  // upload config
  const DEFAULT_UPLOAD_CONFIG = {
    is_public: false,
    requires_document_processing: true,
    shares: advisorId ? [advisorId] : [],
  };

  // handle file upload
  const handleUploadFile = async (
    file: RcFile,
    onSuccess: (response: FilehubFilesData) => void,
    onError: (error: Error) => void,
  ) => {
    try {
      setUploading(true);
      setUploadProgress(0);

      const formData = new FormData();

      const presignedUrlResponse: getFilehubPresignedURLResponse =
        await getFilehubPresignedURL(
          {
            is_public: DEFAULT_UPLOAD_CONFIG.is_public,
            requires_document_processing:
              DEFAULT_UPLOAD_CONFIG.requires_document_processing,
            shares: DEFAULT_UPLOAD_CONFIG.shares,
            file_name: file.name,
            file_size: file.size,
            file_type: file.type,
          },
          file,
        );

      Object.entries(presignedUrlResponse.presigned_body.fields).forEach(
        ([key, value]) => {
          formData.append(key, value);
        },
      );
      formData.append("file", file);

      const xhr = new XMLHttpRequest();
      xhr.open("POST", presignedUrlResponse.presigned_body.url, true);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percent);
        }
      };

      xhr.onload = async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = await confirmUploadFile(
              presignedUrlResponse.data.key,
              file.name,
              presignedUrlResponse.data.owner_cognito_id,
              presignedUrlResponse.data.shares || [],
            );

            // add new file to list
            const newFile = { ...response.file, source: "sent" as const };
            const updatedSentFiles = [newFile, ...sentFiles];

            setSentFiles(updatedSentFiles);
            setFilteredSentFiles(
              updatedSentFiles.filter(
                (file) =>
                  !searchKeyword ||
                  file.filename
                    .toLowerCase()
                    .includes(searchKeyword.toLowerCase()),
              ),
            );

            // update combined data
            setCombinedData([newFile, ...combinedData]);
            setFilteredData([newFile, ...filteredData]);

            // update count
            setSentTotalCount(updatedSentFiles.length);
            setSentTotalPage(Math.ceil(updatedSentFiles.length / sentPageSize));

            message.success(`${file.name} uploaded successfully`);
            onSuccess(response.file);

            setUploadProgress(0);
          } catch (error) {
            console.error("Confirm upload failed:", error);
            message.error(`Failed to confirm upload for ${file.name}`);
            onError(new Error("Confirm upload failed"));
          }
        } else {
          console.error("Upload failed with status:", xhr.status);
          message.error(`Upload failed with status: ${xhr.status}`);
          onError(new Error(`Upload failed with status: ${xhr.status}`));
        }
        setUploading(false);
      };

      xhr.onerror = () => {
        console.error("Network error during upload");
        message.error("Network error during upload");
        onError(new Error("Network error during upload"));
        setUploading(false);
      };

      xhr.send(formData);
    } catch (error) {
      console.error("Upload preparation failed:", error);
      message.error(`${file.name} upload preparation failed`);
      onError(error as Error);
      setUploading(false);
    }
  };

  // swipe handlers for mobile cards
  const handleTouchStart = (_key: string, e: React.TouchEvent) => {
    swipeJustHappenedRef.current = false;
    touchStartXRef.current = e.touches[0].clientX;
    touchCurrentXRef.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchCurrentXRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (key: string) => {
    const diff = touchStartXRef.current - touchCurrentXRef.current;
    if (diff > SWIPE_THRESHOLD) {
      // swiped left → reveal actions
      swipeJustHappenedRef.current = true;
      setSwipedCardKey(key);
    } else if (diff < -SWIPE_THRESHOLD) {
      // swiped right → hide actions
      swipeJustHappenedRef.current = true;
      setSwipedCardKey(null);
    }
  };

  // mobile card component for file items
  const renderMobileFileCard = (
    file: EnhancedFilehubFilesData,
    showDelete: boolean,
  ) => {
    const isSwiped = swipedCardKey === file.key;
    return (
      <div
        className={`mobile-file-card-wrapper${isSwiped ? " swiped" : ""}`}
        key={file.key}
      >
        <div
          className="mobile-file-card"
          onTouchStart={(e) => handleTouchStart(file.key, e)}
          onTouchMove={handleTouchMove}
          onTouchEnd={() => handleTouchEnd(file.key)}
          onClick={() => {
            if (swipeJustHappenedRef.current) {
              swipeJustHappenedRef.current = false;
              return;
            }
            if (!isSwiped) {
              setSwipedCardKey(file.key);
            } else {
              setSwipedCardKey(null);
            }
          }}
        >
          <div className="mobile-file-card-info">
            <div className="mobile-file-card-name">{file.filename}</div>
            <div className="mobile-file-card-meta">
              <span>{dayjs(file.upload_at).format("MMM D, YYYY")}</span>
              <span className="mobile-file-card-dot">•</span>
              <span>{file.source === "sent" ? "Sent" : "Received"}</span>
            </div>
          </div>
          <RightOutlined
            className="mobile-file-card-arrow"
            style={{ fontSize: 24 }}
          />
        </div>
        <div className="mobile-file-card-actions-revealed">
          <button
            className="mobile-action-btn download"
            onClick={(e) => {
              e.stopPropagation();
              handleDownload(file);
            }}
          >
            <CloudDownloadOutlined
              style={{
                fontSize: 20,
                color: theme === "dark" ? "#ffffff" : "#3A7BD5",
              }}
            />
          </button>
          {showDelete && (
            <button
              className="mobile-action-btn delete"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(file);
              }}
            >
              <DeleteOutlined style={{ fontSize: 20, color: "#D2737E" }} />
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderMobileApexCard = (doc: ApexDocument) => (
    <div
      className="mobile-file-card-wrapper"
      key={doc.document_id}
      onClick={() => handleApexDownload(doc)}
    >
      <div className="mobile-file-card">
        <div className="mobile-file-card-info">
          <div className="mobile-file-card-name">{doc.document_name}</div>
          <div className="mobile-file-card-meta">
            <span>{doc.document_date}</span>
          </div>
        </div>
        <RightOutlined
          className="mobile-file-card-arrow"
          style={{ fontSize: 24 }}
        />
      </div>
    </div>
  );

  // mobile list renderer for combined "All" data
  const renderMobileAllList = () => {
    const paginated = getPaginatedData(filteredData, currentPage, pageSize);
    return (
      <div className="mobile-file-list">
        {loading ? (
          <div className="mobile-file-list-loading">Loading...</div>
        ) : paginated.length === 0 ? (
          <div className="mobile-file-list-empty">No files found</div>
        ) : (
          paginated.map((file) =>
            renderMobileFileCard(file, file.source === "sent"),
          )
        )}
        {filteredData.length > pageSize && (
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={filteredData.length}
            onChange={handlePaginationChange}
            size="small"
            className="mobile-pagination"
          />
        )}
      </div>
    );
  };

  const renderMobileReceivedList = () => {
    const paginated = getPaginatedData(
      filteredReceivedFiles,
      receivedPage,
      receivedPageSize,
    );
    return (
      <div className="mobile-file-list">
        {loading ? (
          <div className="mobile-file-list-loading">Loading...</div>
        ) : paginated.length === 0 ? (
          <div className="mobile-file-list-empty">No files found</div>
        ) : (
          paginated.map((file) => renderMobileFileCard(file, false))
        )}
        {filteredReceivedFiles.length > receivedPageSize && (
          <Pagination
            current={receivedPage}
            pageSize={receivedPageSize}
            total={receivedTotalCount}
            onChange={handleReceivedPaginationChange}
            size="small"
            className="mobile-pagination"
          />
        )}
      </div>
    );
  };

  const renderMobileSentList = () => {
    const paginated = getPaginatedData(
      filteredSentFiles,
      sentPage,
      sentPageSize,
    );
    return (
      <div className="mobile-file-list">
        {loading ? (
          <div className="mobile-file-list-loading">Loading...</div>
        ) : paginated.length === 0 ? (
          <div className="mobile-file-list-empty">No files found</div>
        ) : (
          paginated.map((file) => renderMobileFileCard(file, true))
        )}
        {filteredSentFiles.length > sentPageSize && (
          <Pagination
            current={sentPage}
            pageSize={sentPageSize}
            total={sentTotalCount}
            onChange={handleSentPaginationChange}
            size="small"
            className="mobile-pagination"
          />
        )}
      </div>
    );
  };

  const renderMobileApexList = () => (
    <div className="mobile-file-list">
      {loading ? (
        <div className="mobile-file-list-loading">Loading...</div>
      ) : apexDocuments.length === 0 ? (
        <div className="mobile-file-list-empty">No documents found</div>
      ) : (
        apexDocuments.map((doc) => renderMobileApexCard(doc))
      )}
    </div>
  );

  const renderMobileTaxCard = (doc: TaxReturnDoc) => (
    <div
      className="mobile-file-card-wrapper"
      key={doc.key}
      onClick={() => handleTaxDownload(doc)}
    >
      <div className="mobile-file-card">
        <div className="mobile-file-card-info">
          <div className="mobile-file-card-name">{doc.name}</div>
          <div className="mobile-file-card-meta">
            <span>Tax Year {doc.year}</span>
          </div>
        </div>
        <RightOutlined
          className="mobile-file-card-arrow"
          style={{ fontSize: 24 }}
        />
      </div>
    </div>
  );

  const renderMobileTaxList = () => (
    <div className="mobile-file-list">
      {loading ? (
        <div className="mobile-file-list-loading">Loading...</div>
      ) : taxDocuments.length === 0 ? (
        <div className="mobile-file-list-empty">No documents found</div>
      ) : (
        taxDocuments.map((doc) => renderMobileTaxCard(doc))
      )}
    </div>
  );

  // mobile filter dropdown options
  const filterOptions: { key: string; label: string }[] = [
    { key: "all", label: "All" },
    { key: "shared-with-me", label: "Shared with me" },
    { key: "shared-by-me", label: "Shared by me" },
    ...(!firmDataEnabled
      ? [{ key: "apex-statements", label: "Account Documents" }]
      : []),
    ...(taxDocuments.length > 0
      ? [{ key: "tax-documents", label: "Tax Documents" }]
      : []),
  ];

  const activeFilterLabel = filterOptions.find(
    (opt) => opt.key === activeTab,
  )?.label;

  const filterDropdownItems = filterOptions.map((opt) => ({
    key: opt.key,
    label: opt.label,
  }));

  // tab definition
  const items: TabsProps["items"] = [
    {
      key: "all",
      label: "All",
      children: isMobile ? (
        renderMobileAllList()
      ) : (
        <Table
          columns={allColumns}
          dataSource={getPaginatedData(filteredData, currentPage, pageSize).map(
            (file) => ({ ...file, key: file.key }),
          )}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: filteredData.length,
            onChange: handlePaginationChange,
            showTotal: (total) => `Total ${total} items`,
          }}
          // onChange={handleSort}
          loading={loading}
        />
      ),
    },
    {
      key: "shared-with-me",
      label: "Shared with me",
      children: isMobile ? (
        renderMobileReceivedList()
      ) : (
        <Table
          columns={documentColumns}
          dataSource={getPaginatedData(
            filteredReceivedFiles,
            receivedPage,
            receivedPageSize,
          ).map((file) => ({ ...file, key: file.key }))}
          pagination={false}
          footer={() => (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <Pagination
                current={receivedPage}
                pageSize={receivedPageSize}
                total={receivedTotalCount}
                onChange={handleReceivedPaginationChange}
                showTotal={(total) =>
                  `Total ${total} items, ${receivedTotalPage} pages`
                }
              />
            </div>
          )}
          // onChange={(pagination, filters, sorter) => handleSort(sorter)}
          loading={loading}
        />
      ),
    },
    {
      key: "shared-by-me",
      label: "Shared by me",
      children: isMobile ? (
        renderMobileSentList()
      ) : (
        <Table
          columns={documentColumns}
          dataSource={getPaginatedData(
            filteredSentFiles,
            sentPage,
            sentPageSize,
          ).map((file) => ({ ...file, key: file.key }))}
          pagination={false}
          footer={() => (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <Pagination
                current={sentPage}
                pageSize={sentPageSize}
                total={sentTotalCount}
                onChange={handleSentPaginationChange}
                showTotal={(total) =>
                  `Total ${total} items, ${sentTotalPage} pages`
                }
              />
            </div>
          )}
          // onChange={(pagination, filters, sorter) => handleSort(sorter)}
          loading={loading}
        />
      ),
    },
    ...(!firmDataEnabled
      ? [
          {
            key: "apex-statements",
            label: "Account Documents",
            children: isMobile ? (
              renderMobileApexList()
            ) : (
              <Table
                columns={apexDocumentColumns}
                dataSource={apexDocuments.map((doc) => ({
                  ...doc,
                  key: doc.document_id,
                }))}
                pagination={{
                  pageSize: 10,
                  showTotal: (total) => `Total ${total} statements`,
                }}
                loading={loading}
              />
            ),
          },
        ]
      : []),
    ...(taxDocuments.length > 0
      ? [
          {
            key: "tax-documents",
            label: "Tax Documents",
            children: isMobile ? (
              renderMobileTaxList()
            ) : (
              <Table
                columns={taxDocumentColumns}
                dataSource={taxDocuments}
                pagination={{
                  pageSize: 10,
                  showTotal: (total) => `Total ${total} documents`,
                }}
                loading={loading}
              />
            ),
          },
        ]
      : []),
  ];

  if (error) {
    return <Alert message="Error" description={error} type="error" showIcon />;
  }

  const noAdvisorWarning = !advisorId ? (
    <Alert
      message="Your account does not have a specified advisor. Please contact your advisor."
      type="warning"
      showIcon
      style={{ marginBottom: "20px" }}
    />
  ) : null;

  const containerClass = `file-hub-container ${theme}-theme`;

  return (
    <div className={containerClass}>
      {noAdvisorWarning}

      {isMobile && (
        <button
          className="mobile-header-upload"
          onClick={() => setIsUploadModalVisible(true)}
          aria-label="Upload files"
        >
          <CloudUploadOutlined
            style={{
              fontSize: 20,
              color: theme === "dark" ? "#ffffff" : "#000000",
            }}
          />
        </button>
      )}

      <div className="documents-section">
        <div className="documents-header">
          <div className="title">Documents</div>
          <div className="search-container">
            <Input
              className="file-search-input"
              placeholder="Search files..."
              prefix={<SearchOutlined />}
              value={searchKeyword}
              onChange={(e) => handleSearch(e.target.value)}
            />
            <Button
              type="primary"
              icon={
                <img
                  src={UploadIcon}
                  alt="Upload"
                  style={{
                    width: 24,
                    height: 24,
                    verticalAlign: "middle",
                  }}
                />
              }
              onClick={() => setIsUploadModalVisible(true)}
              className="upload-button"
            >
              Upload files
            </Button>
          </div>
        </div>

        {isMobile && (
          <div className="mobile-filter-bar">
            <Dropdown
              menu={{
                items: filterDropdownItems,
                onClick: ({ key }) => handleTabChange(key),
                selectedKeys: [activeTab],
              }}
              trigger={["click"]}
            >
              <button className="mobile-filter-button" type="button">
                <FilterOutlined className="mobile-filter-icon" />
                <span>Filters</span>
                <DownOutlined className="mobile-filter-chevron" />
              </button>
            </Dropdown>
            {activeTab !== "all" && (
              <div className="mobile-filter-chip">
                <span>{activeFilterLabel}</span>
                <CloseOutlined
                  className="mobile-filter-chip-close"
                  role="button"
                  tabIndex={0}
                  aria-label="Clear filter"
                  onClick={() => handleTabChange("all")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleTabChange("all");
                    }
                  }}
                />
              </div>
            )}
          </div>
        )}

        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={items}
          className="file-tabs"
        />

        {/* <div className="documents-table">
          <Spin spinning={loading} tip="Loading documents...">
            <Table 
              columns={documentColumns} 
              dataSource={paginatedDocuments.map(file => ({ ...file, key: file.key }))}
              rowClassName={rowClassName}
              pagination={false}
              footer={() => (
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                  <Pagination
                    current={currentPage}
                    pageSize={pageSize}
                    total={filteredData.length}
                    onChange={handlePaginationChange}
                    showTotal={(total) => `Total ${total} items`}
                  />
                </div>
              )} 
            />
          </Spin>
        </div> */}
      </div>

      <Modal
        title="Secure Upload"
        open={isUploadModalVisible}
        onCancel={() => {
          setIsUploadModalVisible(false);
          setPendingFile(null);
          setFileList([]);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsUploadModalVisible(false);
              setPendingFile(null);
              setFileList([]);
            }}
            style={
              theme === "dark"
                ? {
                    color: "#FFFFFF",
                    borderColor: "#FFFFFF",
                    background: "transparent",
                  }
                : {
                    // color: "#000000",
                    // borderColor: "#000000",
                    // background: "transparent",
                  }
            }
          >
            Cancel
          </Button>,
          <Button
            key="upload"
            type="primary"
            onClick={() => {
              if (pendingFile) {
                handleUploadFile(
                  pendingFile,
                  () => {
                    setIsUploadModalVisible(false);
                    setPendingFile(null);
                    setFileList([]);
                  },
                  (error) => {
                    console.error("Upload failed:", error);
                  },
                );
              }
            }}
            disabled={!pendingFile}
            style={
              theme === "dark"
                ? {
                    backgroundColor: "#FFFFFF",
                    color: "#000000",
                    borderColor: "#FFFFFF",
                  }
                : {
                    // backgroundColor: "#FFFFFF",
                    // color: "#000000",
                    // borderColor: "#FFFFFF",
                  }
            }
          >
            Upload File
          </Button>,
        ]}
        width={600}
      >
        <div className="upload-area">
          <p className="text">
            Use this area to securely upload documents.
          </p>

          {uploading && (
            <Progress
              percent={uploadProgress}
              status="active"
              style={{ marginBottom: 16 }}
            />
          )}

          <Upload.Dragger
            customRequest={({ file, onSuccess = () => {} }) => {
              setPendingFile(file as RcFile);
              onSuccess("ok");
            }}
            fileList={fileList}
            onChange={handleChange}
            multiple={false}
            showUploadList={true}
            disabled={uploading}
          >
            <p className="ant-upload-drag-icon">
              <img src={File} alt="Upload" />
            </p>
            <p className="ant-upload-text">
              Drag files here or click to upload (max size: 100MB)
            </p>
          </Upload.Dragger>
        </div>
      </Modal>

      {/* <div className="bottom-section">
        <div className="recent-uploads">
          <div className="title">
            Recent Uploads
          </div>
          <Spin spinning={loading} tip="Loading uploads...">
            <List
              className="uploads-list"
              dataSource={sentFiles.slice(0, 10)}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Button 
                      key="preview" 
                      type="link" 
                      onClick={() => handlePreview(item)}
                      icon={<EyeOutlined />}
                    />,
                    <Button 
                      key="download" 
                      type="link" 
                      onClick={() => handleDownload(item)}
                      icon={<DownloadOutlined />}
                    />,
                    <Button 
                      key="delete" 
                      type="link" 
                      danger
                      onClick={() => handleDelete(item)}
                      icon={<DeleteOutlined />}
                    />
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <>
                        {item.filename.split('.').slice(0, -1).join('.')} 
                        <span className="file-date">
                          ({dayjs(item.upload_at).format('DD MMM')})
                        </span>
                      </>
                    }
                  />
                </List.Item>
              )}
            />
          </Spin>
        </div>
        
        <div className="upload-area">
          <div className="title">
            Secure Upload
          </div>
          
          {uploading && (
            <Progress 
              percent={uploadProgress} 
              status="active" 
              style={{ marginBottom: 16 }}
            />
          )}
          
          <Upload.Dragger
            customRequest={customRequest}
            fileList={fileList}
            onChange={handleChange}
            multiple={false}
            showUploadList={false}
            disabled={uploading}
          >
            <p className="ant-upload-drag-icon">
              <CloudUploadOutlined style={{ fontSize: 60 }} />
            </p>
            <p className="ant-upload-text">Drag & drop files here or</p>
            <p className="ant-upload-hint">
              Use this secure area to upload documents to share with your advisory team.
            </p>
          </Upload.Dragger>
        </div>
      </div> */}
    </div>
  );
};

export default FileHub;
